<!--
  Header Edit Overlay — on-header live editing, drawn directly over the REAL
  top-bar controls.

  Shown while the Header Builder drawer is open (headerLayout.editing) under the
  New Design preset. It measures the real header elements (each tagged with a
  `data-hkey` "iid" in App.vue — a control key, or "spacer:<id>" for a divider)
  and paints, exactly on top of each one:
    · a dashed frame that ALSO captures pointer events, so the real control is
      NOT clickable while editing and the whole element is draggable as an
      object (grab anywhere on it),
    · an eye toggle for controls (hide → Hidden tray on the right) or an ✕ for
      dividers (remove).
  Dragging reorders the live preview so the real header reflects it instantly.
  There is no separate list — you edit the header itself.
-->
<template>
  <div v-if="show" class="heo-root">
    <!-- dashed frame + controls for each visible header element -->
    <div
      v-for="r in rects"
      :key="r.iid"
      class="heo-frame"
      :class="{ dragging: dragIid === r.iid && moved, 'is-spacer': r.isSpacer }"
      :style="{ left: r.x + 'px', top: r.y + 'px', width: r.w + 'px', height: r.h + 'px' }"
      @pointerdown="startDrag(r.iid, $event)"
    >
      <span class="heo-grip"><v-icon size="12">mdi-drag</v-icon></span>

      <!-- Arrow reorder — SAME element/class as the eye (a <span class="heo-act">),
           so it renders as the exact same white circle (a <button> got oval UA/
           Vuetify sizing). Pinned to the bottom-left / bottom-right corners. -->
      <span
        class="heo-act heo-arrow heo-arrow--l"
        :class="{ 'heo-arrow--off': r.iid === firstIid }"
        :title="t('headerBuilder.move_left')"
        @pointerdown.stop
        @click.stop="nudge(r.iid, -1)"
      >
        <v-icon size="12">mdi-arrow-left</v-icon>
      </span>
      <span
        class="heo-act heo-arrow heo-arrow--r"
        :class="{ 'heo-arrow--off': r.iid === lastIid }"
        :title="t('headerBuilder.move_right')"
        @pointerdown.stop
        @click.stop="nudge(r.iid, 1)"
      >
        <v-icon size="12">mdi-arrow-right</v-icon>
      </span>

      <!-- Divider: drag handle to resize its width live -->
      <span
        v-if="r.isSpacer"
        class="heo-resize"
        :title="t('headerBuilder.width_resize')"
        @pointerdown.stop.prevent="startResize(r.iid, $event)"
      ></span>

      <!-- Divider: remove (✕) -->
      <span
        v-if="r.isSpacer"
        class="heo-act heo-act--del"
        :title="t('headerBuilder.remove_divider')"
        @pointerdown.stop
        @click.stop="removeSpacer(r.iid)"
      >
        <v-icon size="12">mdi-close</v-icon>
      </span>
      <!-- Control: hide (eye) unless it is core -->
      <span
        v-else-if="!metaOf(r.key).core"
        class="heo-act heo-act--eye"
        :title="t('headerBuilder.edit_mode.hide')"
        @pointerdown.stop
        @click.stop="hideKey(r.key)"
      >
        <v-icon size="12">mdi-eye-off-outline</v-icon>
      </span>
      <span v-else class="heo-act heo-act--lock" :title="t('headerBuilder.core_hint')">
        <v-icon size="11">mdi-lock</v-icon>
      </span>
    </div>

    <!-- drop indicator -->
    <div v-if="indicator" class="heo-slot" :style="{ left: indicator.x + 'px', top: indicator.y + 'px', height: indicator.h + 'px' }"></div>

    <!-- floating ghost following the cursor -->
    <div v-if="dragIid && moved" class="heo-ghost" :style="{ left: dragPos.x + 'px', top: dragPos.y + 'px' }">
      <v-icon size="16">{{ ghostIcon }}</v-icon>
      <span>{{ ghostLabel }}</span>
    </div>

    <!-- edit-mode badge -->
    <div class="heo-badge">
      <v-icon size="14" class="mr-1">mdi-pencil-ruler</v-icon>
      {{ t('headerBuilder.edit_mode.badge') }}
      <span class="heo-badge__hint">{{ t('headerBuilder.edit_mode.hint') }}</span>
    </div>

    <!-- hidden tray: restore hidden elements -->
    <div v-if="hiddenKeys.length" class="heo-tray">
      <span class="heo-tray__label"><v-icon size="13">mdi-eye-off-outline</v-icon>{{ t('headerBuilder.zones.hidden') }}</span>
      <button
        v-for="k in hiddenKeys"
        :key="k"
        class="heo-tray__chip"
        :title="t('headerBuilder.edit_mode.show')"
        @click="showKey(k)"
      >
        <v-icon size="14">{{ iconOf(k) }}</v-icon>
        <span>{{ labelOf(k) }}</span>
        <v-icon size="12" class="heo-tray__plus">mdi-plus</v-icon>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useHeaderLayoutStore } from '@/stores/headerLayout'

const { t } = useI18n()
const hb = useHeaderLayoutStore()

const show = computed(() => hb.editing && hb.active)

const ICONS = {
  search: 'mdi-magnify',
  presence: 'mdi-circle-slice-8',
  quick_create: 'mdi-plus-circle-outline',
  mute: 'mdi-volume-high',
  notifications: 'mdi-bell-outline',
  task_notifications: 'mdi-clipboard-check-outline',
  chat: 'mdi-chat-outline',
  dialer: 'mdi-phone',
  call_alerts: 'mdi-bell-ring-outline',
  balance: 'mdi-diamond-stone',
  ai: 'mdi-robot-outline',
}
function iconOf(key) { return ICONS[key] || 'mdi-checkbox-blank-circle-outline' }
function labelOf(key) { return t(`headerBuilder.items.${key}`) }
function metaOf(key) { return hb.metaOf(key) }

const hiddenKeys = computed(() => hb.zonesSnapshot.hidden)

// Edge detection for the ←/→ arrow buttons (disable at the ends of the row).
const firstIid = computed(() => hb.headerIids[0])
const lastIid = computed(() => hb.headerIids[hb.headerIids.length - 1])
function nudge(iid, dir) { hb.nudgeIid(iid, dir); remeasure() }

// --- measure the real header elements -------------------------------------
const rects = ref([])
function measure() {
  const out = []
  document.querySelectorAll('[data-hkey]').forEach((el) => {
    const iid = el.getAttribute('data-hkey')
    if (!iid) return
    const isSpacer = iid.startsWith('spacer:')
    const key = isSpacer ? null : iid
    if (!isSpacer && !hb.inHeader(key)) return
    const rc = el.getBoundingClientRect()
    if (rc.width === 0 && rc.height === 0) return
    // Guarantee a comfortable hit area (thin dividers otherwise unclickable).
    let x = rc.left; let w = rc.width
    if (w < 20) { x -= (20 - w) / 2; w = 20 }
    out.push({ iid, key, isSpacer, x, y: rc.top, w, h: rc.height })
  })
  out.sort((a, b) => a.x - b.x)
  rects.value = out
}
function remeasure() { nextTick(() => { measure(); setTimeout(measure, 80) }) }

// --- drag reorder (pointer based) -----------------------------------------
const dragIid = ref(null)
const dragPos = ref({ x: 0, y: 0 })
const indicator = ref(null)
const dragStart = ref({ x: 0, y: 0 })
const moved = ref(false)
const DRAG_THRESHOLD = 4 // px — a plain click must NOT reorder

const ghostLabel = computed(() => {
  if (!dragIid.value) return ''
  return dragIid.value.startsWith('spacer:') ? t('headerBuilder.divider') : labelOf(dragIid.value)
})
const ghostIcon = computed(() => {
  if (!dragIid.value) return ''
  return dragIid.value.startsWith('spacer:') ? 'mdi-drag-vertical' : iconOf(dragIid.value)
})

function computeIndicator(x) {
  const rs = rects.value.filter((r) => r.iid !== dragIid.value)
  let target = null
  let before = true
  for (const r of rs) {
    if (x < r.x + r.w / 2) { target = r; before = true; break }
  }
  if (!target && rs.length) { target = rs[rs.length - 1]; before = false }
  if (!target) { indicator.value = null; return }
  indicator.value = {
    iid: target.iid,
    before,
    x: before ? target.x - 7 : target.x + target.w + 1,
    y: target.y - 2,
    h: target.h + 4,
  }
}

function startDrag(iid, e) {
  e.preventDefault()
  dragIid.value = iid
  dragStart.value = { x: e.clientX, y: e.clientY }
  dragPos.value = { x: e.clientX, y: e.clientY }
  moved.value = false
  window.addEventListener('pointermove', onMove)
  window.addEventListener('pointerup', onUp)
}
function onMove(e) {
  dragPos.value = { x: e.clientX, y: e.clientY }
  if (!moved.value) {
    const dx = e.clientX - dragStart.value.x
    const dy = e.clientY - dragStart.value.y
    if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return // still a click, not a drag
    moved.value = true
  }
  computeIndicator(e.clientX)
}
function onUp() {
  window.removeEventListener('pointermove', onMove)
  window.removeEventListener('pointerup', onUp)
  // Only reorder on a real drag (moved past threshold) — a plain click is inert.
  if (dragIid.value && moved.value && indicator.value) {
    hb.moveIid(dragIid.value, indicator.value.iid, indicator.value.before)
  }
  dragIid.value = null
  indicator.value = null
  moved.value = false
  remeasure()
}

function hideKey(key) { hb.toggleHidden(key); remeasure() }
function showKey(key) { hb.toggleHidden(key); remeasure() }
function removeSpacer(iid) { hb.removeSpacer(iid.slice(7)); remeasure() }

// --- divider resize (drag the right-edge handle to set width live) --------
const resizeIid = ref(null)
const resizeStart = ref({ x: 0, w: 16 })
function startResize(iid, e) {
  resizeIid.value = iid
  const sp = hb.spacerItems.find((s) => s.id === iid.slice(7))
  resizeStart.value = { x: e.clientX, w: sp ? sp.width : 16 }
  window.addEventListener('pointermove', onResize)
  window.addEventListener('pointerup', endResize)
}
function onResize(e) {
  if (!resizeIid.value) return
  const dx = e.clientX - resizeStart.value.x
  hb.setSpacerWidth(resizeIid.value.slice(7), resizeStart.value.w + dx)
  remeasure()
}
function endResize() {
  window.removeEventListener('pointermove', onResize)
  window.removeEventListener('pointerup', endResize)
  resizeIid.value = null
}

// --- lifecycle: keep frames aligned ---------------------------------------
function onWinChange() { if (show.value) measure() }

watch(show, (v) => { if (v) remeasure() })
watch(() => hb.previewVersion, () => { if (show.value) remeasure() })

onMounted(() => {
  window.addEventListener('resize', onWinChange)
  window.addEventListener('scroll', onWinChange, true)
  if (show.value) remeasure()
})
onBeforeUnmount(() => {
  window.removeEventListener('resize', onWinChange)
  window.removeEventListener('scroll', onWinChange, true)
  window.removeEventListener('pointermove', onMove)
  window.removeEventListener('pointerup', onUp)
  window.removeEventListener('pointermove', onResize)
  window.removeEventListener('pointerup', endResize)
})
</script>

<style scoped>
/* Root does not block the header; the FRAMES (below) do the blocking so the
   real controls are inert while editing. */
.heo-root { position: fixed; inset: 0; z-index: 1200; pointer-events: none; }

.heo-frame {
  position: fixed;
  border: 1.5px dashed rgba(30, 58, 95, 0.55);
  border-radius: 10px;
  box-shadow: 0 0 0 3px rgba(77, 212, 172, 0.10);
  background: rgba(77, 212, 172, 0.04);
  cursor: grab;
  pointer-events: auto; /* shield: swallow clicks + be the drag handle */
  touch-action: none;
}
.heo-frame:hover { border-color: rgba(30, 58, 95, 0.85); }
.heo-frame:active { cursor: grabbing; }
.heo-frame.dragging { opacity: .35; }
.heo-frame.is-spacer { border-style: dotted; background: rgba(30, 58, 95, 0.06); }

.heo-grip {
  position: absolute; top: -12px; left: 50%; transform: translateX(-50%);
  background: #1e3a5f; color: #fff; border-radius: 5px;
  display: inline-flex; align-items: center; justify-content: center;
  padding: 0 2px; height: 15px;
}
.heo-act {
  position: absolute; top: -11px; right: -11px;
  width: 20px; height: 20px; border-radius: 50%;
  background: #fff; border: 1px solid rgba(0, 0, 0, 0.15); color: #6b7280;
  display: inline-flex; align-items: center; justify-content: center;
  cursor: pointer;
}
.heo-act--eye:hover { color: #1e3a5f; border-color: #1e3a5f; }
.heo-act--del { color: #ef4444; border-color: rgba(239, 68, 68, 0.5); }
.heo-act--del:hover { background: #ef4444; color: #fff; }
.heo-act--lock { cursor: default; }

/* ←/→ nudge buttons — WHITE circle with a black arrow, exactly like the eye
   (heo-act), small (18px), pinned to the bottom corners of the frame. White +
   border keeps adjacent elements' arrows visually separate instead of merging
   into one dark oval. */
/* Arrows reuse .heo-act (the eye's circle): white 20px round, border, shadow.
   Here we only PLACE them at the bottom corners, paint the glyph dark, and give
   the hover fill. Same shape as the eye — never an oval. */
.heo-arrow {
  top: auto; bottom: -11px; color: #1f2937; z-index: 3;
}
.heo-arrow--l { left: -11px; right: auto; }
.heo-arrow--r { right: -11px; left: auto; }
.heo-arrow:hover { background: #1e3a5f; color: #fff; border-color: #1e3a5f; }
.heo-arrow--off { opacity: .3; pointer-events: none; cursor: default; }

/* Divider resize handle — grab the right edge and drag to set width. */
.heo-resize {
  position: absolute; top: 50%; right: -5px; transform: translateY(-50%);
  width: 8px; height: 62%; min-height: 16px; border-radius: 4px;
  background: #4dd4ac; border: 1px solid rgba(0, 0, 0, 0.12);
  cursor: ew-resize; pointer-events: auto;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
}
.heo-resize:hover { background: #3bbf98; }

.heo-slot {
  position: fixed; width: 4px; border-radius: 4px;
  background: #4dd4ac; box-shadow: 0 0 0 2px rgba(77, 212, 172, 0.35);
}
.heo-ghost {
  position: fixed; transform: translate(10px, -10px);
  display: inline-flex; align-items: center; gap: 6px;
  background: #1e3a5f; color: #fff; border-radius: 8px;
  padding: 5px 10px; font-size: 12px; font-weight: 700;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.28);
}

.heo-badge {
  position: fixed; top: 70px; left: 12px;
  display: inline-flex; align-items: center;
  background: #4dd4ac; color: #0b3b30;
  font-size: 11px; font-weight: 800; letter-spacing: .03em; text-transform: uppercase;
  padding: 5px 12px; border-radius: 999px; pointer-events: none;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.15);
}
.heo-badge__hint { margin-left: 8px; font-weight: 600; text-transform: none; opacity: .85; }

.heo-tray {
  position: fixed; top: 68px; right: 12px;
  display: inline-flex; align-items: center; gap: 8px; flex-wrap: wrap;
  max-width: 460px;
  background: rgba(255, 255, 255, 0.98); border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px; padding: 8px 12px; pointer-events: auto;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.14);
}
.heo-tray__label {
  display: inline-flex; align-items: center; gap: 3px;
  font-size: 10.5px; font-weight: 800; text-transform: uppercase;
  color: #6b7280; letter-spacing: .03em;
}
.heo-tray__chip {
  display: inline-flex; align-items: center; gap: 5px;
  background: #f4f6f9; border: 1px dashed #b6bcc7; color: #1f2937;
  border-radius: 999px; padding: 4px 9px; font-size: 12px; font-weight: 700;
  cursor: pointer;
}
.heo-tray__chip:hover { border-color: #1e3a5f; background: #eef7f4; }
.heo-tray__plus { color: #0b7a5e; }

@media (min-width: 960px) {
  .heo-tray { right: 480px; }
}
</style>
